import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Typography,
} from "@mui/material";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { FONT_HEADING } from "../../../theme";
import {
  checkZohoAuth,
  getZohoAuthUrl,
  revokeZoho,
  verifyZohoAuth,
  registerExtWidget,
} from "../../../APICalling/zohoAdminApi";
import {
  ZOHO_CONN,
  SHOPIFY_SHOP_URL_HEADER_KEY,
  buildShopifyShopUrlHeader,
  EXTENSION_IDENTIFIER,
  SHOPIFY_ORG_CONN,
} from "../../../config/serviceConstant";
import {
  JOURNEY_WIDGET_NAME,
  JOURNEY_WIDGET_DESCRIPTION,
  JOURNEY_WIDGET_URL,
  JOURNEY_WIDGET_TYPE,
} from "../../../config/zohoConstant";
import {
  authCheckDuration,
  getOpenSnakErrorData,
  getOpenSnakSuccessData,
  maxAuthCheck,
} from "../../../config/otherConstant";
import { ensureDefaultMappingsAndWebhooks } from "../../../util/ensureDefaultSetup";
import { getShopifyStores } from "../../../APICalling/shopifyAdminApi";
import {
  getSettingByType,
  upsertSetting,
} from "../../../APICalling/settingApi";

let checkAuthCount;

// Register the Record Journey widget at most once per session — re-mounts and
// the mount-time auth re-verification shouldn't re-POST it.
let journeyWidgetRegistered = false;

// ── Helper: status column in the hero card ──
function StatusCol({ label, valueNode, sub, link }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: "#71717a",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          mb: "4px",
        }}
      >
        {label}
      </Typography>
      <Box>{valueNode}</Box>
      {sub && (
        <Typography sx={{ fontSize: 12, color: "#a1a1aa", mt: "2px" }}>
          {sub}
        </Typography>
      )}
      {link && (
        <Typography
          component="button"
          sx={{
            display: "block",
            fontSize: 12,
            fontWeight: 600,
            color: "#0f172a",
            background: "none",
            border: "none",
            cursor: "pointer",
            p: 0,
            mt: "4px",
            fontFamily: "inherit",
            textDecoration: "underline",
            textUnderlineOffset: "2px",
            "&:hover": { color: "#000" },
          }}
        >
          {link}
        </Typography>
      )}
    </Box>
  );
}

// ── Helper: detail key/value tile in the Connection Details card ──
function DetailItem({ icon, label, value, truncate, link }) {
  return (
    <Box
      sx={{
        bgcolor: "#fafafa",
        border: "1px solid #f4f4f5",
        borderRadius: "10px",
        p: "12px 14px",
        minWidth: 0,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "4px", color: "#71717a" }}>
        {icon}
        <Typography sx={{ fontSize: 12, color: "#71717a", fontWeight: 500 }}>
          {label}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 600,
          color: "#09090b",
          ...(truncate
            ? {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }
            : {}),
        }}
        title={truncate ? value : undefined}
      >
        {value}
      </Typography>
      {link && (
        <Typography
          component="button"
          sx={{
            display: "block",
            fontSize: 12,
            fontWeight: 600,
            color: "#0f172a",
            background: "none",
            border: "none",
            cursor: "pointer",
            p: 0,
            mt: "4px",
            fontFamily: "inherit",
            textDecoration: "underline",
            textUnderlineOffset: "2px",
            "&:hover": { color: "#000" },
          }}
        >
          {link}
        </Typography>
      )}
    </Box>
  );
}

// ── Main Tab ──
export default function ZohoAuthTab({
  orgId,
  apiKey,
  zApiKey,
  dataCenterUrl,
  dataCenter,
  orgVarMaps,
  shop,
  shopifyStores,
  zohoAuth,
  setZohoAuth,
  setSnack,
  setSetupInProgress,
  onSettingsCreated,
}) {
  const [crmLoading, setCrmLoading] = useState(true);
  const [revokeTarget, setRevokeTarget] = useState(null); // 'crm'

  const shopUrlHeader = buildShopifyShopUrlHeader(shop);

  const zohoHeaders = {
    orgid: orgId,
    connname: ZOHO_CONN,
    apikey: apiKey,
    datacenterurl: dataCenterUrl,
    datacenter: dataCenter,
    zapikey: zApiKey,
    [SHOPIFY_SHOP_URL_HEADER_KEY]: shopUrlHeader,
  };


  const settingAuth = { orgId, apiKey, dataCenterUrl, shop };

  // Register the Record Journey related-list widget with Zoho once CRM auth is
  // confirmed. apiDomain falls back to the request's datacenterurl inside
  // registerExtWidget; the session guard prevents redundant POSTs.
  const registerJourneyWidget = async () => {
    if (journeyWidgetRegistered) return;
    const resp = await registerExtWidget(zohoHeaders, {
      name: JOURNEY_WIDGET_NAME,
      description: JOURNEY_WIDGET_DESCRIPTION,
      url: JOURNEY_WIDGET_URL,
      type: JOURNEY_WIDGET_TYPE,
    });
    if (resp) {
      journeyWidgetRegistered = true;
    } else {
      console.warn("[Journey] Widget registration returned no data (see network/console for the error).");
    }
  };

  // Resolve the full list of Shopify stores — prefer the live prop, fall back
  // to orgVarMaps, and finally to a /tokens fetch so fresh loads still work.
  const resolveAllStoreDomains = async () => {
    const fromProp = (shopifyStores || [])
      .map((s) => s?.domain)
      .filter(Boolean);
    if (fromProp.length > 0) return fromProp;

    const fromMaps = (orgVarMaps?.stores || [])
      .map((s) => s?.domain)
      .filter(Boolean);
    if (fromMaps.length > 0) return fromMaps;

    const tokensHeaders = {
      orgid: orgId,
      connname: SHOPIFY_ORG_CONN,
      apikey: apiKey,
      datacenterurl: dataCenterUrl,
      datacenter: dataCenter,
    };
    const stores = await getShopifyStores(tokensHeaders);
    return (stores || []).map((s) => s?.domain).filter(Boolean);
  };

  // Run default setup for a given service across ALL connected stores.
  // Each store gets its own settings + webhooks using per-store headers.
  const runDefaultSetup = async (service) => {
    try {
      const storeDomains = await resolveAllStoreDomains();
      if (storeDomains.length === 0) {
        console.warn("[DefaultSetup] No shop domain found, skipping setup");
        return;
      }

      let totalCreated = 0;
      const allWarnings = [];

      for (const storeDomain of storeDomains) {
        const perStoreShopifyHeaders = {
          orgid: orgId,
          connname: EXTENSION_IDENTIFIER,
          apikey: apiKey,
          datacenterurl: dataCenterUrl,
          datacenter: dataCenter,
          [SHOPIFY_SHOP_URL_HEADER_KEY]: buildShopifyShopUrlHeader(storeDomain),
        };
        const perStoreSettingAuth = {
          orgId,
          apiKey,
          dataCenterUrl,
          shop: storeDomain,
        };

        const { created, warnings } = await ensureDefaultMappingsAndWebhooks(
          perStoreSettingAuth,
          perStoreShopifyHeaders,
          storeDomain,
          zApiKey,
          service
        );

        if (created.length > 0) {
          totalCreated += created.length;
        }
        if (warnings.length > 0) {
          console.warn(
            `[DefaultSetup] Warnings for ${service} @ ${storeDomain}:`,
            warnings
          );
          allWarnings.push(`${storeDomain}: ${warnings.join("; ")}`);
        }
      }

      if (totalCreated > 0) onSettingsCreated?.();
      if (allWarnings.length > 0) {
        setSnack(
          getOpenSnakErrorData(`Warning: ${allWarnings.join(" | ")}`, 8000)
        );
      }
    } catch (err) {
      console.error(`[DefaultSetup] Error for ${service}:`, err);
    }
  };

  // Verify all on mount and ensure default settings exist
  useEffect(() => {
    const verifyAll = async () => {
      try {
        const crmOk = await checkZohoAuth(zohoHeaders);
        setZohoAuth(crmOk);
        if (crmOk) {
          await registerJourneyWidget();
          await runDefaultSetup("Zoho CRM");
          await enableCRMSettingsAfterAuth();
        }
      } catch (e) {
        // not authenticated
      }
      setCrmLoading(false);
    };
    verifyAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Safety net: register the Journey widget whenever CRM auth is confirmed —
  // regardless of which flow flipped it true (mount verify, fresh connect, or a
  // parent that already had zohoAuth=true). The connect-flow poll can time out
  // on slow OAuth, so we don't rely on it alone. Session-guarded against dupes.
  useEffect(() => {
    if (zohoAuth) registerJourneyWidget();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zohoAuth]);

  const handleConnectZohoCRM = async () => {
    setCrmLoading(true);
    const zohoAuthUrl = await getZohoAuthUrl(zohoHeaders);
    if (!zohoAuthUrl) {
      setCrmLoading(false);
      setSnack(getOpenSnakErrorData("Failed to get Zoho auth URL.", 5000));
      return;
    }

    checkAuthCount = 1;
    const myAuthCheckInterval = setInterval(async () => {
      const { isAuthenticated } = await verifyZohoAuth(zohoHeaders);
      if (isAuthenticated) {
        setZohoAuth(true);
        setCrmLoading(false);
        clearInterval(myAuthCheckInterval);
        setSnack(getOpenSnakSuccessData("Zoho CRM authenticated!"));
        // Block the UI until every store's default settings + webhooks exist.
        setSetupInProgress?.(true);
        try {
          await registerJourneyWidget();
          await runDefaultSetup("Zoho CRM");
          await enableCRMSettingsAfterAuth();
        } finally {
          setSetupInProgress?.(false);
        }
      }
      if (checkAuthCount++ > maxAuthCheck) {
        setCrmLoading(false);
        clearInterval(myAuthCheckInterval);
        setSnack(
          getOpenSnakErrorData(
            "Timed out. Please authenticate and refresh.",
            10000
          )
        );
      }
    }, authCheckDuration);

    const newwindow = window.open(zohoAuthUrl, "zohoAuth", "height=640,width=1024");
    if (window.focus && newwindow) newwindow.focus();
  };

  // Parse a stored setting's schema, tolerating stringified payloads.
  const parseSchema = (s) => {
    let schema = s?.settingSchema || s?.setting_schema || {};
    if (typeof schema === "string") {
      try { schema = JSON.parse(schema); } catch { schema = {}; }
    }
    return schema || {};
  };

  // Bulk-update the `enabled` flag on systemDefined settings that match a
  // per-setting predicate. Preserves the existing schema otherwise.
  const setEnabledWhere = async (predicate) => {
    const list = await getSettingByType(settingAuth, "systemDefined");
    await Promise.all(
      (list || []).map((s) => {
        const schema = parseSchema(s);
        const nextEnabled = predicate(schema, s);
        if (nextEnabled === null || schema.enabled === nextEnabled) return null;
        const sId = s.setting_id || s.settingId || s.id;
        return upsertSetting(settingAuth, {
          settingId: sId,
          settingSchema: { ...schema, enabled: nextEnabled },
          moduleApiName: s.moduleApiName || s.module_name || schema.module || "",
          settingType: "systemDefined",
        });
      })
    );
  };

  // ── Revoke: just disable all CRM settings (don't delete) ──
  const disableAllCRMSettings = async () => {
    try {
      await setEnabledWhere((schema) =>
        schema.service === "Zoho CRM" ? false : null
      );
    } catch (err) {
      console.error("[Revoke] Disable error:", err);
    }
  };

  // The 6 default optional triggers we leave disabled on re-auth. Everything
  // else (the 3 mandatory creates + any user-added custom settings) is enabled.
  const DEFAULT_OPTIONAL_TRIGGER_APINAMES = new Set([
    "customers/update",
    "customers/delete",
    "orders/updated",
    "orders/delete",
    "products/update",
    "products/delete",
  ]);
  const MANDATORY_CREATE_TRIGGER_APINAMES = new Set([
    "customers/create",
    "orders/create",
    "products/create",
  ]);

  // ── Re-auth: enable mandatory creates + custom settings ──
  const enableCRMSettingsAfterAuth = async () => {
    try {
      await setEnabledWhere((schema) => {
        if (schema.service !== "Zoho CRM") return null;
        // CRM → Shopify outbound rules are an explicit user opt-in (seeded
        // disabled) — never force-enable them on (re-)auth.
        if (schema.direction === "CRM2Shopify") return null;
        const trig = schema.triggerApiName;
        if (MANDATORY_CREATE_TRIGGER_APINAMES.has(trig)) return true;
        if (DEFAULT_OPTIONAL_TRIGGER_APINAMES.has(trig)) return null; // leave as-is
        return true; // custom user-created — enable
      });
    } catch (err) {
      console.error("[ReAuth] Enable error:", err);
    }
  };

  const handleRevoke = async () => {
    if (revokeTarget === "crm") {
      // Disable (don't delete) all CRM settings so webhooks keep their
      // setting_ids intact for when the user re-authenticates.
      await disableAllCRMSettings();
      const resp = await revokeZoho(zohoHeaders);
      if (resp) {
        setZohoAuth(false);
        setSnack(getOpenSnakSuccessData("Zoho CRM revoked!"));
      }
    }
    setRevokeTarget(null);
  };

  // ── NOT authenticated: centered CTA ──
  if (!zohoAuth) {
    return (
      <>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "calc(100vh - 160px)",
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "12px",
              bgcolor: "#e0f2fe",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2.5,
              color: "#0369a1",
            }}
          >
            <HubOutlinedIcon sx={{ fontSize: 32 }} />
          </Box>
          <Typography
            sx={{
              fontFamily: FONT_HEADING,
              fontSize: 20,
              fontWeight: 600,
              color: "#09090b",
              mb: "6px",
              letterSpacing: "-0.02em",
            }}
          >
            Connect Your Zoho CRM
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              color: "#71717a",
              mb: 3,
              maxWidth: 380,
              lineHeight: 1.5,
            }}
          >
            Authorize the extension to read and write Zoho CRM records so your
            Shopify stores can sync.
          </Typography>
          <Button
            variant="contained"
            disabled={crmLoading}
            onClick={handleConnectZohoCRM}
            sx={{
              textTransform: "none",
              px: "28px",
              py: "10px",
              fontSize: 15,
              fontWeight: 600,
              bgcolor: "#0f172a",
              boxShadow: "none",
              "&:hover": { bgcolor: "#000", boxShadow: "none" },
            }}
          >
            {crmLoading && (
              <CircularProgress size={14} sx={{ mr: 1, color: "#fff" }} />
            )}
            Authenticate Zoho
          </Button>
        </Box>
      </>
    );
  }

  // ── AUTHENTICATED: hero + connection details + revoke + activity ──
  const apiUrlDisplay = dataCenterUrl || "—";
  const dataCenterDisplay = dataCenter || "—";

  // Placeholder data — wire to backend later.
  const connectedBy = "Admin User";
  const lastVerified = "a few minutes ago";
  const edition = "Enterprise";

  return (
    <>
      {/* ── Hero card: Zoho CRM connection summary ── */}
      <Box
        sx={{
          bgcolor: "#fff",
          border: "1px solid #e4e4e7",
          borderRadius: "12px",
          p: 2.5,
          mb: 2.5,
          display: "flex",
          alignItems: "center",
          gap: 3,
          flexWrap: "wrap",
        }}
      >
        {/* Identity (icon + name + pill + description) */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, flex: 1, minWidth: 280 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "12px",
              bgcolor: "#e0f2fe",
              color: "#0369a1",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <HubOutlinedIcon sx={{ fontSize: 26 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: "2px" }}>
              <Typography
                sx={{
                  fontFamily: FONT_HEADING,
                  fontSize: 17,
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  color: "#09090b",
                }}
              >
                Zoho CRM
              </Typography>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  fontSize: 12,
                  fontWeight: 600,
                  px: "8px",
                  py: "1px",
                  borderRadius: "9999px",
                  bgcolor: crmLoading ? "#fef3c7" : "#dcfce7",
                  color: crmLoading ? "#92400e" : "#166534",
                }}
              >
                {crmLoading && (
                  <CircularProgress size={9} sx={{ color: "#92400e" }} />
                )}
                {crmLoading ? "Verifying..." : "Connected"}
              </Box>
            </Box>
            <Typography sx={{ fontSize: 13, color: "#71717a", lineHeight: 1.5 }}>
              Your Zoho CRM account is connected and ready to sync data with
              your Shopify stores.
            </Typography>
          </Box>
        </Box>

        {/* Right: status badges */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(2, 1fr)" },
            gap: 3,
            flexShrink: 0,
            pl: { xs: 0, md: 2 },
            borderLeft: { xs: "none", md: "1px solid #f4f4f5" },
            ml: { xs: 0, md: "auto" },
          }}
        >
          <StatusCol
            label="Connection Status"
            valueNode={
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: "4px", color: "#166534", fontWeight: 600, fontSize: 14 }}>
                <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#22c55e" }} />
                Healthy
              </Box>
            }
            sub={`Last verified: ${lastVerified}`}
          />
          <StatusCol
            label="Connected by"
            valueNode={
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#09090b" }}>
                {connectedBy}
              </Typography>
            }
            sub="Admin User"
          />
        </Box>
      </Box>

      {/* ── Connection Details ── */}
      <Box
        sx={{
          bgcolor: "#fff",
          border: "1px solid #e4e4e7",
          borderRadius: "12px",
          p: 2.5,
          mb: 2.5,
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT_HEADING,
            fontSize: 15,
            fontWeight: 700,
            letterSpacing: "-0.01em",
            color: "#09090b",
            mb: 2,
          }}
        >
          Connection Details
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
            gap: 1.5,
          }}
        >
          <DetailItem
            icon={<PublicOutlinedIcon sx={{ fontSize: 17 }} />}
            label="Data Center"
            value={dataCenterDisplay}
          />
          <DetailItem
            icon={<BadgeOutlinedIcon sx={{ fontSize: 17 }} />}
            label="Organization ID"
            value={orgId || "—"}
          />
          <DetailItem
            icon={<WorkspacePremiumOutlinedIcon sx={{ fontSize: 17 }} />}
            label="Edition"
            value={edition}
          />
          <DetailItem
            icon={<LinkOutlinedIcon sx={{ fontSize: 17 }} />}
            label="API URL"
            value={apiUrlDisplay}
            truncate
          />
        </Box>
      </Box>

      {/* ── Revoke Access banner ── */}
      <Box
        sx={{
          bgcolor: "#fffbeb",
          border: "1px solid #fde68a",
          borderRadius: "12px",
          p: "14px 18px",
          mb: 2.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <WarningAmberOutlinedIcon sx={{ fontSize: 20, color: "#b45309" }} />
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography
            sx={{
              fontFamily: FONT_HEADING,
              fontSize: 14,
              fontWeight: 700,
              color: "#92400e",
            }}
          >
            Revoke Access
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#92400e", opacity: 0.9 }}>
            Revoking access will stop all synchronization between Shopify and
            Zoho CRM. Existing CRM records will not be deleted.
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          disabled={crmLoading}
          onClick={() => setRevokeTarget("crm")}
          sx={{
            textTransform: "none",
            fontSize: 14,
            fontWeight: 600,
            px: "16px",
            py: "5px",
            color: "#b45309",
            bgcolor: "#fff",
            borderColor: "#f59e0b",
            "&:hover": {
              bgcolor: "#fef3c7",
              borderColor: "#d97706",
            },
            "&.Mui-disabled": {
              color: "#d4d4d8",
              borderColor: "#e4e4e7",
              bgcolor: "#fafafa",
            },
          }}
        >
          {crmLoading ? "Verifying..." : "Revoke Access"}
        </Button>
      </Box>

      {/* Revoke Dialog */}
      <Dialog
        open={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        PaperProps={{
          sx: {
            borderRadius: "14px",
            maxWidth: 460,
            width: "100%",
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          {/* Warning icon */}
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              bgcolor: "#fee2e2",
              color: "#dc2626",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <WarningAmberOutlinedIcon sx={{ fontSize: 26 }} />
          </Box>

          {/* Title */}
          <Typography
            sx={{
              fontFamily: FONT_HEADING,
              fontSize: 17,
              fontWeight: 700,
              color: "#09090b",
              letterSpacing: "-0.02em",
              mb: 0.5,
            }}
          >
            Revoke Zoho CRM connection?
          </Typography>

          {/* Description */}
          <Typography sx={{ fontSize: 14, color: "#52525b", lineHeight: 1.55, mb: 2 }}>
            Revoking will stop syncing between Shopify and Zoho CRM. The
            following will be disabled:
          </Typography>

          {/* Impact list */}
          <Box
            sx={{
              bgcolor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "10px",
              p: "12px 14px",
              mb: 2.5,
            }}
          >
            {[
              "Workflows, widgets, and custom buttons",
              "Related-list views inside CRM records",
              "Field-mapping rules and settings",
            ].map((line, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  fontSize: 14,
                  color: "#991b1b",
                  py: "3px",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    bgcolor: "#dc2626",
                    flexShrink: 0,
                    mt: "7px",
                  }}
                />
                {line}
              </Box>
            ))}
          </Box>

          <Typography sx={{ fontSize: 13, color: "#71717a", mb: 2.5 }}>
            Your existing CRM records won't be deleted. You can reconnect any
            time to resume syncing.
          </Typography>

          {/* Actions */}
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button
              onClick={() => setRevokeTarget(null)}
              variant="outlined"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                color: "#3f3f46",
                borderColor: "#e4e4e7",
                "&:hover": { borderColor: "#a1a1aa", bgcolor: "#fafafa" },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRevoke}
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                bgcolor: "#dc2626",
                boxShadow: "none",
                "&:hover": { bgcolor: "#b91c1c", boxShadow: "none" },
              }}
            >
              Revoke Connection
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
}
